import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { exportFunnel, importFunnelNodes } from '@/services/api/funnel';
import { Copy, Download, Upload, Check, AlertCircle } from 'lucide-react';
import type { Funnel, FunnelExportData } from '@/types/funnel';

interface ExportImportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Se fornecido, abre no modo export com os dados desse funnel */
    exportFunnel?: Funnel | null;
    /** Lista de funnels para escolher o destino no import */
    funnels: Funnel[];
}

export default function ExportImportModal({
    open,
    onOpenChange,
    exportFunnel: exportFunnelProp,
    funnels,
}: ExportImportModalProps) {
    const [mode, setMode] = useState<'export' | 'import'>('export');
    const [json, setJson] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [targetFunnelId, setTargetFunnelId] = useState<number | ''>('');
    const [clearExisting, setClearExisting] = useState(true);
    const [jsonError, setJsonError] = useState<string | null>(null);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Reset state when modal opens
    useEffect(() => {
        if (!open) return;

        if (exportFunnelProp) {
            setMode('export');
            setJson('');
            setLoading(true);
            exportFunnel(exportFunnelProp.id)
                .then((data) => {
                    setJson(JSON.stringify(data, null, 2));
                })
                .catch((err) => {
                    toast({
                        variant: 'destructive',
                        title: 'Erro ao exportar',
                        description: err.response?.data?.detail || 'Erro ao exportar funil.',
                    });
                })
                .finally(() => setLoading(false));
        } else {
            setMode('import');
            setJson('');
            setTargetFunnelId('');
            setClearExisting(true);
            setJsonError(null);
        }

        setCopied(false);
    }, [open, exportFunnelProp]);

    // Validate JSON on change (import mode)
    useEffect(() => {
        if (mode !== 'import' || !json.trim()) {
            setJsonError(null);
            return;
        }
        try {
            const parsed = JSON.parse(json);
            if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
                setJsonError('JSON deve conter um array "nodes"');
            } else {
                setJsonError(null);
            }
        } catch {
            setJsonError('JSON inv\u00e1lido');
        }
    }, [json, mode]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(json);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({ title: 'JSON copiado!' });
    };

    const importMutation = useMutation({
        mutationFn: importFunnelNodes,
        onSuccess: () => {
            toast({ title: 'Nodes importados com sucesso!' });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao importar',
                description: error.response?.data?.detail || 'Erro ao importar nodes.',
            });
        },
    });

    const handleImport = () => {
        if (!targetFunnelId || jsonError) return;

        try {
            const parsed: FunnelExportData = JSON.parse(json);
            importMutation.mutate({
                funnel_id: targetFunnelId as number,
                clear_existing: clearExisting,
                nodes: parsed.nodes,
                transitions: parsed.transitions || [],
            });
        } catch {
            setJsonError('JSON inv\u00e1lido');
        }
    };

    const targetFunnel = funnels.find((f) => f.id === targetFunnelId);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold text-[#0f172a]">
                        {mode === 'export' ? 'Exportar Funil' : 'Importar Nodes'}
                    </DialogTitle>
                    <p className="text-sm text-gray-500 mt-1">
                        {mode === 'export'
                            ? 'Copie o JSON abaixo e cole em outro funil para duplicar os nodes.'
                            : 'Cole o JSON exportado de outro funil para importar os nodes.'}
                    </p>
                </DialogHeader>

                {/* Mode tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setMode('export')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'export'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Download className="w-4 h-4" />
                        Exportar
                    </button>
                    <button
                        onClick={() => setMode('import')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'import'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Upload className="w-4 h-4" />
                        Importar
                    </button>
                </div>

                {/* Export: funnel selector (when switching to export without a pre-selected funnel) */}
                {mode === 'export' && !exportFunnelProp && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Selecione o funil para exportar
                        </label>
                        <select
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={targetFunnelId}
                            onChange={(e) => {
                                const id = Number(e.target.value);
                                setTargetFunnelId(id);
                                if (id) {
                                    setLoading(true);
                                    exportFunnel(id)
                                        .then((data) => setJson(JSON.stringify(data, null, 2)))
                                        .catch(() => toast({ variant: 'destructive', title: 'Erro ao exportar' }))
                                        .finally(() => setLoading(false));
                                }
                            }}
                        >
                            <option value="">Selecione...</option>
                            {funnels.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.nome} (#{f.id})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Import: target funnel selector */}
                {mode === 'import' && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Funil destino
                            </label>
                            <select
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={targetFunnelId}
                                onChange={(e) => setTargetFunnelId(Number(e.target.value) || '')}
                            >
                                <option value="">Selecione o funil destino...</option>
                                {funnels.map((f) => (
                                    <option key={f.id} value={f.id}>
                                        {f.nome} (#{f.id}) {f.is_active ? '' : '- Inativo'}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={clearExisting}
                                onChange={(e) => setClearExisting(e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            Limpar nodes existentes antes de importar
                        </label>
                        {targetFunnel?.is_active && (
                            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                Este funil est\u00e1 ativo. Recomendamos desativ\u00e1-lo antes de importar.
                            </div>
                        )}
                    </div>
                )}

                {/* JSON editor */}
                <div className="flex-1 min-h-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]" />
                        </div>
                    ) : (
                        <div className="relative">
                            <textarea
                                value={json}
                                onChange={(e) => setJson(e.target.value)}
                                readOnly={mode === 'export'}
                                placeholder={mode === 'import' ? 'Cole o JSON exportado aqui...' : ''}
                                className={`w-full h-80 font-mono text-xs border rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    jsonError
                                        ? 'border-red-300 bg-red-50/50'
                                        : 'border-gray-200 bg-gray-50'
                                } ${mode === 'export' ? 'cursor-default' : ''}`}
                                spellCheck={false}
                            />
                            {mode === 'export' && json && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 gap-1.5"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-3.5 h-3.5 text-green-500" />
                                            Copiado
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3.5 h-3.5" />
                                            Copiar
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                    {jsonError && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {jsonError}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Fechar
                    </Button>
                    {mode === 'import' && (
                        <Button
                            onClick={handleImport}
                            disabled={!targetFunnelId || !json.trim() || !!jsonError || importMutation.isPending}
                        >
                            {importMutation.isPending ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            Importar Nodes
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
